/**
 * Respack validation exactly like Phira (W1-08; skin-and-assets.md S§2.1-S§2.3, DESIGN.md §7.5):
 *
 *  - info.yml is read like serde_yaml 0.9 with camelCase keys: unknown keys ignored, duplicate keys refused,
 *    required `name`, `author`, `hitFx`, `holdAtlas`, `holdAtlasMH`; strings take any scalar's text; integers are
 *    plain (unquoted) decimal / 0x / 0o / 0b scalars in range; floats plain numbers or .inf / .nan; booleans
 *    plain true / false (any capitalisation YAML allows); a quoted number or boolean is a type error;
 *  - Phira's verify(): name non-empty and at most 100 UTF-8 bytes, description at most 1000 bytes,
 *    1 <= cols * rows <= 10240 (saturating u32 multiply);
 *  - images: every one of the nine must exist and decode (PNG, JPEG, GIF, BMP or WebP content, whatever the
 *    `.png` name says; checked from the file header, which also gives the size), and the hold atlas slices
 *    must leave room for the body: tail + head < image height, for hold.png and hold_mh.png;
 *  - sounds: for click, drag, flick and ending the FIRST existing of .ogg, .wav, .mp3 must decode (Ogg Vorbis,
 *    WAV, MP3 or FLAC content); `hold.*` is never used by Phira.
 * Results are MessageRefs (namespace 'respack'); nothing here decodes pixels or audio samples.
 *
 * Limit of a pure module: "decodes" is judged from the file header only, so a truncated or corrupt image (or sound)
 * with a valid header passes here. Whoever really decodes the files must report the failures it meets: the skin
 * decoder (render/skin/decodeSkin.ts) refuses a pack whose image the browser cannot decode with
 * `skin.imageUndecodable` (no bitmap is kept), and whoever decodes the hit sounds must report one that fails as
 * `respack.error.soundUndecodable` (Phira refuses such a pack). Neither may assume that a pack accepted here is
 * fully decodable.
 */

import { isMap, isScalar, isSeq, parseDocument } from 'yaml';
import type { MessageRef } from '../../core/util/types';
import type { ResPackInfo } from './types';

/** Phira's defaults for the optional fields (S§2.2). */
export const RESPACK_DEFAULTS = Object.freeze({
  description: '',
  hitFxDuration: 0.5,
  hitFxScale: 1,
  hitFxRotate: false,
  hitFxTinted: true,
  hideParticles: false,
  holdKeepHead: false,
  holdRepeat: false,
  holdCompact: false,
  colorPerfect: 0xe1ffec9f,
  colorGood: 0xebb4e1ff,
});

export const RESPACK_NAME_MAX_BYTES = 100;
export const RESPACK_DESCRIPTION_MAX_BYTES = 1000;
export const RESPACK_HIT_FX_MAX_FRAMES = 10240;

const U16_MAX = 0xffff;
const U32_MAX = 0xffffffff;

/** UTF-8 byte length (Rust `str::len`). */
export function utf8Length(s: string): number {
  return new TextEncoder().encode(s).length;
}

// ---------------------------------------------------------------------------------------------------------------
// info.yml
// ---------------------------------------------------------------------------------------------------------------

type Expected = 'text' | 'wholeNumber' | 'number' | 'bool' | 'pair';

class FieldError extends Error {
  readonly ref: MessageRef;
  constructor(ref: MessageRef) {
    super(ref.key);
    this.ref = ref;
  }
}

interface ScalarText {
  /** The scalar as written (plain scalars keep their source text, e.g. "0xE1FFEC9F"). */
  readonly text: string;
  /** Quoted or block scalar: always text for serde_yaml. */
  readonly quoted: boolean;
}

function scalarText(node: unknown): ScalarText | null {
  if (!isScalar(node)) return null;
  const quoted = node.type === 'QUOTE_DOUBLE' || node.type === 'QUOTE_SINGLE';
  const block = node.type === 'BLOCK_FOLDED' || node.type === 'BLOCK_LITERAL';
  const value =
    typeof node.value === 'string' || typeof node.value === 'number' || typeof node.value === 'boolean'
      ? String(node.value)
      : '';
  const text = quoted || block ? value : (node.source ?? value);
  return { text, quoted: quoted || block };
}

const INT_RE = /^[-+]?(?:0x[0-9a-fA-F]+|0o[0-7]+|0b[01]+|[0-9]+)$/;
const FLOAT_RE = /^[-+]?(?:\.[0-9]+|[0-9]+(?:\.[0-9]*)?)(?:[eE][-+]?[0-9]+)?$/;
const SPECIAL_FLOAT: Readonly<Record<string, number>> = {
  '.inf': Number.POSITIVE_INFINITY,
  '.Inf': Number.POSITIVE_INFINITY,
  '.INF': Number.POSITIVE_INFINITY,
  '+.inf': Number.POSITIVE_INFINITY,
  '+.Inf': Number.POSITIVE_INFINITY,
  '+.INF': Number.POSITIVE_INFINITY,
  '-.inf': Number.NEGATIVE_INFINITY,
  '-.Inf': Number.NEGATIVE_INFINITY,
  '-.INF': Number.NEGATIVE_INFINITY,
  '.nan': Number.NaN,
  '.NaN': Number.NaN,
  '.NAN': Number.NaN,
};

/** A YAML integer scalar's value (decimal, 0x, 0o, 0b), or null. */
export function parseYamlInt(text: string): number | null {
  if (!INT_RE.test(text)) return null;
  const neg = text.startsWith('-');
  const body = text.replace(/^[-+]/, '');
  let v: number;
  if (body.startsWith('0x')) v = parseInt(body.slice(2), 16);
  else if (body.startsWith('0o')) v = parseInt(body.slice(2), 8);
  else if (body.startsWith('0b')) v = parseInt(body.slice(2), 2);
  else v = Number(body);
  return neg ? -v : v;
}

function fieldError(key: string, field: string, params: Record<string, string | number> = {}): FieldError {
  return new FieldError({ key, params: { field, ...params } });
}

function typeError(field: string, expected: Expected): FieldError {
  return fieldError('respack.error.fieldType', field, { expected });
}

function readText(node: unknown, field: string): string {
  const s = scalarText(node);
  if (s === null) throw typeError(field, 'text');
  return s.text;
}

function readInt(node: unknown, field: string, max: number): number {
  const s = scalarText(node);
  if (s === null) throw typeError(field, 'wholeNumber');
  if (s.quoted) throw fieldError('respack.error.quotedValue', field);
  const v = parseYamlInt(s.text);
  if (v === null) throw typeError(field, 'wholeNumber');
  if (v < 0 || v > max) throw fieldError('respack.error.outOfRange', field, { max });
  return v;
}

function readFloat(node: unknown, field: string): number {
  const s = scalarText(node);
  if (s === null) throw typeError(field, 'number');
  if (s.quoted) throw fieldError('respack.error.quotedValue', field);
  const special = SPECIAL_FLOAT[s.text];
  if (special !== undefined) return special;
  const int = parseYamlInt(s.text);
  if (int !== null) return Math.fround(int);
  if (!FLOAT_RE.test(s.text)) throw typeError(field, 'number');
  return Math.fround(Number(s.text));
}

function readBool(node: unknown, field: string): boolean {
  const s = scalarText(node);
  if (s === null) throw typeError(field, 'bool');
  if (s.quoted) throw fieldError('respack.error.quotedValue', field);
  if (s.text === 'true' || s.text === 'True' || s.text === 'TRUE') return true;
  if (s.text === 'false' || s.text === 'False' || s.text === 'FALSE') return false;
  throw typeError(field, 'bool');
}

function readPair(node: unknown, field: string, max: number): readonly [number, number] {
  if (!isSeq(node) || node.items.length !== 2) throw typeError(field, 'pair');
  return [readInt(node.items[0], field, max), readInt(node.items[1], field, max)];
}

export interface InfoParse {
  readonly info: ResPackInfo | null;
  readonly errors: readonly MessageRef[];
  readonly warnings: readonly MessageRef[];
}

/** An 8-digit hex colour with alpha 00: Phira cannot tell it from RGB and draws it opaque. */
function alphaZeroColour(node: unknown): boolean {
  const s = scalarText(node);
  return s !== null && !s.quoted && /^\+?0x00[0-9a-fA-F]{6}$/.test(s.text);
}

/** Reads info.yml bytes like Phira (serde_yaml 0.9, camelCase, unknown keys ignored). */
export function parseRespackInfo(bytes: Uint8Array): InfoParse {
  const errors: MessageRef[] = [];
  const warnings: MessageRef[] = [];
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return { info: null, errors: [{ key: 'respack.error.infoNotUtf8' }], warnings };
  }
  const doc = parseDocument(text, { uniqueKeys: true });
  const firstError = doc.errors[0];
  if (firstError !== undefined) {
    const pos = firstError.linePos?.[0];
    const isDuplicate = firstError.code === 'DUPLICATE_KEY';
    errors.push(
      isDuplicate
        ? { key: 'respack.error.duplicateKey', params: { line: pos?.line ?? 0 } }
        : { key: 'respack.error.yaml', params: { line: pos?.line ?? 0, col: pos?.col ?? 0 } },
    );
    return { info: null, errors, warnings };
  }
  const root = doc.contents;
  if (!isMap(root)) return { info: null, errors: [{ key: 'respack.error.notMapping' }], warnings };
  const fields = new Map<string, unknown>();
  for (const pair of root.items) {
    const k = scalarText(pair.key);
    if (k !== null) fields.set(k.text, pair.value);
  }
  const get = <T>(field: string, read: (node: unknown) => T, def?: T): T | null => {
    if (!fields.has(field)) {
      if (def !== undefined) return def;
      errors.push({ key: 'respack.error.missingField', params: { field } });
      return null;
    }
    try {
      return read(fields.get(field));
    } catch (e) {
      if (!(e instanceof FieldError)) throw e;
      errors.push(e.ref);
      return null;
    }
  };
  const name = get('name', (n) => readText(n, 'name'));
  const author = get('author', (n) => readText(n, 'author'));
  const description = get('description', (n) => readText(n, 'description'), RESPACK_DEFAULTS.description);
  const hitFx = get('hitFx', (n) => readPair(n, 'hitFx', U32_MAX));
  const hitFxDuration = get('hitFxDuration', (n) => readFloat(n, 'hitFxDuration'), RESPACK_DEFAULTS.hitFxDuration);
  const hitFxScale = get('hitFxScale', (n) => readFloat(n, 'hitFxScale'), RESPACK_DEFAULTS.hitFxScale);
  const hitFxRotate = get('hitFxRotate', (n) => readBool(n, 'hitFxRotate'), RESPACK_DEFAULTS.hitFxRotate);
  const hitFxTinted = get('hitFxTinted', (n) => readBool(n, 'hitFxTinted'), RESPACK_DEFAULTS.hitFxTinted);
  const hideParticles = get('hideParticles', (n) => readBool(n, 'hideParticles'), RESPACK_DEFAULTS.hideParticles);
  const holdAtlas = get('holdAtlas', (n) => readPair(n, 'holdAtlas', U16_MAX));
  const holdAtlasMH = get('holdAtlasMH', (n) => readPair(n, 'holdAtlasMH', U16_MAX));
  const holdKeepHead = get('holdKeepHead', (n) => readBool(n, 'holdKeepHead'), RESPACK_DEFAULTS.holdKeepHead);
  const holdRepeat = get('holdRepeat', (n) => readBool(n, 'holdRepeat'), RESPACK_DEFAULTS.holdRepeat);
  const holdCompact = get('holdCompact', (n) => readBool(n, 'holdCompact'), RESPACK_DEFAULTS.holdCompact);
  const colorPerfect = get('colorPerfect', (n) => readInt(n, 'colorPerfect', U32_MAX), RESPACK_DEFAULTS.colorPerfect);
  const colorGood = get('colorGood', (n) => readInt(n, 'colorGood', U32_MAX), RESPACK_DEFAULTS.colorGood);
  for (const field of ['colorPerfect', 'colorGood']) {
    if (alphaZeroColour(fields.get(field))) warnings.push({ key: 'respack.warning.colorAlphaZero', params: { field } });
  }
  if (
    name === null ||
    author === null ||
    description === null ||
    hitFx === null ||
    hitFxDuration === null ||
    hitFxScale === null ||
    hitFxRotate === null ||
    hitFxTinted === null ||
    hideParticles === null ||
    holdAtlas === null ||
    holdAtlasMH === null ||
    holdKeepHead === null ||
    holdRepeat === null ||
    holdCompact === null ||
    colorPerfect === null ||
    colorGood === null
  ) {
    return { info: null, errors, warnings };
  }
  const info: ResPackInfo = {
    name,
    author,
    description,
    hitFx,
    hitFxDuration,
    hitFxScale,
    hitFxRotate,
    hitFxTinted,
    hideParticles,
    holdAtlas,
    holdAtlasMH,
    holdKeepHead,
    holdRepeat,
    holdCompact,
    colorPerfect,
    colorGood,
  };
  return { info, errors, warnings };
}

/** Phira's `ResPackInfo::verify` rules. */
export function verifyRespackInfo(info: ResPackInfo): MessageRef[] {
  const errors: MessageRef[] = [];
  const nameBytes = utf8Length(info.name);
  if (info.name.length === 0) errors.push({ key: 'respack.error.nameEmpty' });
  else if (nameBytes > RESPACK_NAME_MAX_BYTES) {
    errors.push({ key: 'respack.error.nameTooLong', params: { bytes: nameBytes, max: RESPACK_NAME_MAX_BYTES } });
  }
  const descBytes = utf8Length(info.description);
  if (descBytes > RESPACK_DESCRIPTION_MAX_BYTES) {
    errors.push({
      key: 'respack.error.descriptionTooLong',
      params: { bytes: descBytes, max: RESPACK_DESCRIPTION_MAX_BYTES },
    });
  }
  const frames = Math.min(info.hitFx[0] * info.hitFx[1], U32_MAX);
  if (frames < 1 || frames > RESPACK_HIT_FX_MAX_FRAMES) {
    errors.push({
      key: 'respack.error.hitFxGrid',
      params: { cols: info.hitFx[0], rows: info.hitFx[1], max: RESPACK_HIT_FX_MAX_FRAMES },
    });
  }
  return errors;
}

// ---------------------------------------------------------------------------------------------------------------
// Media headers
// ---------------------------------------------------------------------------------------------------------------

export type RespackImageFormat = 'png' | 'jpeg' | 'gif' | 'bmp' | 'webp';

export interface ImageHeader {
  readonly format: RespackImageFormat;
  readonly width: number;
  readonly height: number;
}

function u16be(b: Uint8Array, i: number): number {
  return ((b[i] ?? 0) << 8) | (b[i + 1] ?? 0);
}
function u16le(b: Uint8Array, i: number): number {
  return (b[i] ?? 0) | ((b[i + 1] ?? 0) << 8);
}
function u24le(b: Uint8Array, i: number): number {
  return (b[i] ?? 0) | ((b[i + 1] ?? 0) << 8) | ((b[i + 2] ?? 0) << 16);
}
function u32be(b: Uint8Array, i: number): number {
  return (((b[i] ?? 0) << 24) >>> 0) + (((b[i + 1] ?? 0) << 16) | ((b[i + 2] ?? 0) << 8) | (b[i + 3] ?? 0));
}
function i32le(b: Uint8Array, i: number): number {
  return (b[i] ?? 0) | ((b[i + 1] ?? 0) << 8) | ((b[i + 2] ?? 0) << 16) | ((b[i + 3] ?? 0) << 24);
}
function ascii(b: Uint8Array, i: number, s: string): boolean {
  for (let k = 0; k < s.length; k++) if (b[i + k] !== s.charCodeAt(k)) return false;
  return true;
}

function jpegSize(b: Uint8Array): ImageHeader | null {
  let i = 2;
  while (i + 9 < b.length) {
    if (b[i] !== 0xff) return null;
    const marker = b[i + 1] ?? 0;
    if (marker === 0xff) {
      i++;
      continue;
    }
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      i += 2;
      continue;
    }
    const len = u16be(b, i + 2);
    // Baseline, extended and progressive DCT frames (what the decoder Phira uses supports).
    if (marker === 0xc0 || marker === 0xc1 || marker === 0xc2) {
      return { format: 'jpeg', height: u16be(b, i + 5), width: u16be(b, i + 7) };
    }
    if (
      marker === 0xd9 ||
      marker === 0xda ||
      (marker >= 0xc3 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc)
    ) {
      return null;
    }
    i += 2 + len;
  }
  return null;
}

/**
 * Format and size from an image file's header, for the formats Phira decodes; null otherwise. A header only:
 * the body can still be truncated or corrupt (see the module header for who reports real decode failures).
 */
export function sniffImage(b: Uint8Array): ImageHeader | null {
  let h: ImageHeader | null = null;
  if (b.length >= 24 && b[0] === 0x89 && ascii(b, 1, 'PNG\r\n\x1a\n') && ascii(b, 12, 'IHDR')) {
    h = { format: 'png', width: u32be(b, 16), height: u32be(b, 20) };
  } else if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) {
    h = jpegSize(b);
  } else if (b.length >= 10 && (ascii(b, 0, 'GIF87a') || ascii(b, 0, 'GIF89a'))) {
    h = { format: 'gif', width: u16le(b, 6), height: u16le(b, 8) };
  } else if (b.length >= 26 && ascii(b, 0, 'BM')) {
    const dib = i32le(b, 14);
    h =
      dib === 12
        ? { format: 'bmp', width: u16le(b, 18), height: u16le(b, 20) }
        : { format: 'bmp', width: Math.abs(i32le(b, 18)), height: Math.abs(i32le(b, 22)) };
  } else if (b.length >= 30 && ascii(b, 0, 'RIFF') && ascii(b, 8, 'WEBP')) {
    if (ascii(b, 12, 'VP8 ') && b[23] === 0x9d && b[24] === 0x01 && b[25] === 0x2a) {
      h = { format: 'webp', width: u16le(b, 26) & 0x3fff, height: u16le(b, 28) & 0x3fff };
    } else if (ascii(b, 12, 'VP8L') && b[20] === 0x2f) {
      const bits = ((b[21] ?? 0) | ((b[22] ?? 0) << 8) | ((b[23] ?? 0) << 16) | ((b[24] ?? 0) << 24)) >>> 0;
      h = { format: 'webp', width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
    } else if (ascii(b, 12, 'VP8X')) {
      h = { format: 'webp', width: u24le(b, 24) + 1, height: u24le(b, 27) + 1 };
    }
  }
  return h !== null && h.width > 0 && h.height > 0 ? h : null;
}

export type RespackSoundCodec = 'vorbis' | 'wav' | 'mp3' | 'flac';

/** Codec of a sound file Phira can decode (Ogg Vorbis, WAV, MP3, FLAC), from its content; null otherwise. */
export function sniffSound(b: Uint8Array): RespackSoundCodec | null {
  if (b.length >= 12 && ascii(b, 0, 'RIFF') && ascii(b, 8, 'WAVE')) return 'wav';
  if (b.length >= 4 && ascii(b, 0, 'fLaC')) return 'flac';
  if (b.length >= 3 && ascii(b, 0, 'ID3')) return 'mp3';
  if (b.length >= 2 && b[0] === 0xff && ((b[1] ?? 0) & 0xe0) === 0xe0) return 'mp3';
  if (b.length >= 36 && ascii(b, 0, 'OggS')) {
    // The first page carries the codec identification header.
    const end = Math.min(b.length, 128);
    for (let i = 26; i + 7 <= end; i++) {
      if (b[i] === 0x01 && ascii(b, i + 1, 'vorbis')) return 'vorbis';
    }
  }
  return null;
}
